/*
  Product page behavior (Watercolor Atlas).
  - Gallery: single source of truth for the active media index. Overlay
    prev/next arrows (wrapping), thumbnail row, and mobile dots all stay in
    sync. Crossfade handled by CSS (.is-active opacity transition).
  - Purchase options: Subscribe & Save / One-Time selection cards. The
    subscribe radio's value mirrors the "Deliver Every" select, so the form
    always posts a single selling_plan param (empty for one-time).
  - Variant + selling-plan selection: updates the ATC price label, hidden id
    input, URL, card prices, Save badge, availability, gallery, and the
    sticky mobile bar.
  - AJAX add to cart via /cart/add.js; dispatches 'cart:updated' on document
    (same contract as dw-quick-add.js) and opens the drawer via window.DWCart.
  - Tabs ([data-dw-tabs]): accessible tabs on desktop/tablet, accordions on mobile.
  - Related products ([data-dw-recommendations]): fetches Shopify product
    recommendations rendered through the section rendering API.
  Idempotent: safe to include more than once per page.
*/
(function () {
  'use strict';

  if (window.__dwProductInit) return;
  window.__dwProductInit = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------------------------ */
  /* Money formatting                                                     */
  /* ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    var key = match ? match[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ------------------------------------------------------------------ */
  /* Main product section                                                 */
  /* ------------------------------------------------------------------ */
  function initProduct(section) {
    if (section.dataset.dwReady) return;
    section.dataset.dwReady = '1';

    var data = null;
    var dataEl = section.querySelector('[data-dw-product-json]');
    try {
      data = dataEl ? JSON.parse(dataEl.textContent) : null;
    } catch (err) {
      data = null;
    }
    var variants = (data && data.variants) || [];
    if (!variants.length) return;

    var moneyFormat = section.dataset.moneyFormat || '${{amount}}';
    var form = section.querySelector('[data-dw-form]');
    var idInput = section.querySelector('[data-dw-id-input]');
    var atc = section.querySelector('[data-dw-atc]');
    var atcLabel = section.querySelector('[data-dw-atc-label]');
    var atcExtra = section.querySelector('[data-dw-atc-extra]');
    var atcPrice = section.querySelector('[data-dw-atc-price]');
    var errorEl = section.querySelector('[data-dw-error]');
    var priceWrap = section.querySelector('[data-dw-price]');
    var qtyInput = section.querySelector('[data-dw-qty]');
    var sticky = section.querySelector('[data-dw-sticky]');
    var stickyPrice = section.querySelector('[data-dw-sticky-price]');
    var stickyAtc = section.querySelector('[data-dw-sticky-atc]');
    var planSelect = section.querySelector('[data-dw-plan-select]');
    var subRadio = section.querySelector('[data-dw-plan-sub]');
    var subPriceEl = section.querySelector('[data-dw-sub-price]');
    var onePriceEl = section.querySelector('[data-dw-onetime-price]');
    var saveBadge = section.querySelector('[data-dw-save-badge]');
    var savePct = section.querySelector('[data-dw-save-pct]');
    var mqMobile = window.matchMedia('(max-width: 767px)');
    var stickySubBtn = section.querySelector('[data-dw-sticky-opt="sub"]');
    var stickyOneBtn = section.querySelector('[data-dw-sticky-opt="one"]');
    var stickySubPrice = section.querySelector('[data-dw-sticky-sub-price]');
    var stickyOnePrice = section.querySelector('[data-dw-sticky-one-price]');

    function currentVariant() {
      var id = idInput ? parseInt(idInput.value, 10) : NaN;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) return variants[i];
      }
      return variants[0];
    }

    function selectedPlanId() {
      var checked = section.querySelector('[data-dw-plan]:checked');
      return checked && checked.value ? parseInt(checked.value, 10) : 0;
    }

    function planFor(variant, planId) {
      if (!variant || !variant.plans) return null;
      for (var i = 0; i < variant.plans.length; i++) {
        if (variant.plans[i].id === planId) return variant.plans[i];
      }
      return null;
    }

    /* ------------------------------------------------------------------
       Gallery. activeIndex is the single source of truth; arrows wrap;
       thumbs and dots sync from it. Built from the DOM once, so it can
       never duplicate entries.
    ------------------------------------------------------------------ */
    var gallery = (function () {
      var medias = Array.prototype.slice.call(section.querySelectorAll('[data-dw-media]'));
      var thumbs = Array.prototype.slice.call(section.querySelectorAll('[data-dw-thumb]'));
      var dots = Array.prototype.slice.call(section.querySelectorAll('[data-dw-dot]'));
      var activeIndex = 0;

      medias.forEach(function (m, i) {
        if (m.classList.contains('is-active')) activeIndex = i;
      });

      function render() {
        medias.forEach(function (m, i) {
          var on = i === activeIndex;
          m.classList.toggle('is-active', on);
          m.classList.remove('is-zoomed');
          if (on) {
            m.removeAttribute('aria-hidden');
          } else {
            m.setAttribute('aria-hidden', 'true');
          }
        });
        thumbs.forEach(function (t, i) {
          var on = i === activeIndex;
          t.classList.toggle('is-active', on);
          t.setAttribute('tabindex', on ? '0' : '-1');
          if (on) {
            t.setAttribute('aria-current', 'true');
          } else {
            t.removeAttribute('aria-current');
          }
        });
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === activeIndex);
        });
      }

      function goTo(index) {
        if (!medias.length) return;
        activeIndex = ((index % medias.length) + medias.length) % medias.length;
        render();
      }

      function goToMediaId(mediaId) {
        for (var i = 0; i < medias.length; i++) {
          if (medias[i].getAttribute('data-media-id') === String(mediaId)) {
            goTo(i);
            return;
          }
        }
      }

      Array.prototype.forEach.call(section.querySelectorAll('[data-dw-nav]'), function (btn) {
        btn.addEventListener('click', function () {
          goTo(activeIndex + (btn.getAttribute('data-dw-nav') === 'next' ? 1 : -1));
        });
      });

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () {
          goTo(i);
        });
      });

      var thumbsWrap = section.querySelector('[data-dw-thumbs]');
      if (thumbsWrap) {
        thumbsWrap.addEventListener('keydown', function (event) {
          var idx = thumbs.indexOf(document.activeElement);
          if (idx === -1) return;
          var next = -1;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (idx + 1) % thumbs.length;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (idx - 1 + thumbs.length) % thumbs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = thumbs.length - 1;
          if (next !== -1) {
            event.preventDefault();
            thumbs[next].focus();
            goTo(next);
          }
        });
      }

      if (medias.length) render();

      return { goToMediaId: goToMediaId };
    })();

    /* --------------- zoom --------------- */
    section.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var zoomBtn = event.target.closest('[data-dw-zoom]');
      if (!zoomBtn || zoomBtn.hasAttribute('data-zoom-off')) return;
      var media = zoomBtn.closest('[data-dw-media]');
      if (!media) return;
      if (media.classList.contains('is-zoomed')) {
        media.classList.remove('is-zoomed');
        return;
      }
      var rect = zoomBtn.getBoundingClientRect();
      var x = 50;
      var y = 50;
      if (event.clientX || event.clientY) {
        x = ((event.clientX - rect.left) / rect.width) * 100;
        y = ((event.clientY - rect.top) / rect.height) * 100;
      }
      media.style.setProperty('--zx', x + '%');
      media.style.setProperty('--zy', y + '%');
      media.classList.add('is-zoomed');
    });

    /* ------------------------------------------------------------------
       Prices. The ATC button label, sticky bar, option-card prices, Save
       badge, and the simple price row (no-plan products) all update here.
    ------------------------------------------------------------------ */
    function updatePrices() {
      var v = currentVariant();
      if (!v) return;
      var planId = selectedPlanId();
      var unit = v.price;
      if (planId) {
        var activePlan = planFor(v, planId);
        if (activePlan) unit = activePlan.price;
      }

      if (atcPrice) atcPrice.textContent = formatMoney(unit, moneyFormat);
      if (stickyPrice) stickyPrice.textContent = formatMoney(unit, moneyFormat);

      /* Subscribe card follows the select's plan even while One-Time is selected. */
      if (planSelect && subPriceEl) {
        var selPlan = planFor(v, parseInt(planSelect.value, 10));
        var subPrice = selPlan ? selPlan.price : v.price;
        subPriceEl.textContent = formatMoney(subPrice, moneyFormat);
        if (saveBadge) {
          var pct = 0;
          if (selPlan && selPlan.price < v.price) {
            pct = Math.round(((v.price - selPlan.price) / v.price) * 100);
          }
          if (pct > 0 && savePct) {
            savePct.textContent = String(pct);
            saveBadge.hidden = false;
          } else {
            saveBadge.hidden = true;
          }
        }
      }
      if (onePriceEl) onePriceEl.textContent = formatMoney(v.price, moneyFormat);
      if (stickyOnePrice) stickyOnePrice.textContent = formatMoney(v.price, moneyFormat);
      if (stickySubPrice && planSelect) {
        var stickySel = planFor(v, parseInt(planSelect.value, 10));
        stickySubPrice.textContent = formatMoney(stickySel ? stickySel.price : v.price, moneyFormat);
      }

      /* Simple price row (rendered only for products without selling plans). */
      if (priceWrap) {
        var compare = v.compare_at_price;
        var html = '<div class="dw-price dw-price--display"><span class="dw-price__main">';
        if (compare && compare > v.price) {
          html += '<s class="dw-price__compare">' + formatMoney(compare, moneyFormat) + '</s>';
        }
        html += '<span class="dw-price__now' + (compare && compare > v.price ? ' dw-price__now--sale' : '') + '">';
        html += formatMoney(v.price, moneyFormat) + '</span></span></div>';
        priceWrap.innerHTML = html;
      }
    }

    function updateAvailability() {
      var v = currentVariant();
      var available = !!(v && v.available);
      /* Sold out keeps the main button enabled: it becomes the
         "Notify Me When Available" trigger for the Back-in-Stock app. */
      if (atc) {
        atc.disabled = false;
        atc.toggleAttribute('data-dw-notify-trigger', !available);
        if (v) atc.setAttribute('data-gw-variant-id', v.id);
      }
      if (stickyAtc) {
        stickyAtc.disabled = false;
        stickyAtc.toggleAttribute('data-dw-notify-trigger', !available);
        if (v) stickyAtc.setAttribute('data-gw-variant-id', v.id);
        stickyAtc.textContent = available
          ? (atc && atc.getAttribute('data-label-default')) || 'Add To Cart'
          : 'Notify Me';
      }
      if (atc && atcLabel) {
        atcLabel.textContent = available
          ? atc.getAttribute('data-label-default')
          : atc.getAttribute('data-label-soldout');
      }
      if (atcExtra) atcExtra.hidden = !available;
    }

    /* --------------- purchase-option cards --------------- */
    function syncOptCards() {
      Array.prototype.forEach.call(section.querySelectorAll('[data-dw-opt]'), function (card) {
        var input = card.querySelector('[data-dw-plan]');
        card.classList.toggle('is-active', !!(input && input.checked));
      });
      var subActive = selectedPlanId() > 0;
      if (stickySubBtn) stickySubBtn.classList.toggle('is-active', subActive);
      if (stickyOneBtn) stickyOneBtn.classList.toggle('is-active', !subActive);
    }

    function pickPlanFromSticky(wantSub) {
      var radios = section.querySelectorAll('[data-dw-plan]');
      var target = null;
      Array.prototype.forEach.call(radios, function (r) {
        var isSub = r.hasAttribute('data-dw-plan-sub');
        if (wantSub && isSub) target = r;
        if (!wantSub && !isSub && r.value === '') target = r;
      });
      if (target && !target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (stickySubBtn) stickySubBtn.addEventListener('click', function () { pickPlanFromSticky(true); });
    if (stickyOneBtn) stickyOneBtn.addEventListener('click', function () { pickPlanFromSticky(false); });

    Array.prototype.forEach.call(section.querySelectorAll('[data-dw-plan]'), function (radio) {
      radio.addEventListener('change', function () {
        syncOptCards();
        updatePrices();
      });
    });

    /* Clicking anywhere on a card (outside its controls) selects it. */
    Array.prototype.forEach.call(section.querySelectorAll('[data-dw-opt]'), function (card) {
      card.addEventListener('click', function (event) {
        if (event.target.closest('select, option, a, button, input, label')) return;
        var input = card.querySelector('[data-dw-plan]');
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    /* The Deliver Every select drives the subscribe radio's value. */
    if (planSelect) {
      planSelect.addEventListener('change', function () {
        if (subRadio) {
          subRadio.value = planSelect.value;
          if (!subRadio.checked) {
            subRadio.checked = true;
            syncOptCards();
          }
        }
        updatePrices();
      });
    }

    /* --------------- variant options --------------- */
    function setVariant(v) {
      if (!v) return;
      if (idInput) idInput.value = v.id;
      if (v.featured_media_id) gallery.goToMediaId(v.featured_media_id);
      var url = section.dataset.productUrl;
      var count = parseInt(section.dataset.variantCount, 10) || 1;
      if (url && count > 1 && window.history && window.history.replaceState) {
        window.history.replaceState({}, '', url + '?variant=' + v.id);
      }
      updatePrices();
      updateAvailability();
    }

    function onOptionChange() {
      var selected = [];
      var fieldsets = section.querySelectorAll('[data-dw-option]');
      Array.prototype.forEach.call(fieldsets, function (fs) {
        var checked = fs.querySelector('input:checked');
        selected[parseInt(fs.getAttribute('data-option-index'), 10)] = checked ? checked.value : null;
      });
      var match = null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = true;
        for (var j = 0; j < v.options.length; j++) {
          if (selected[j] != null && v.options[j] !== selected[j]) {
            ok = false;
            break;
          }
        }
        if (ok) {
          match = v;
          break;
        }
      }
      if (match) setVariant(match);
    }

    Array.prototype.forEach.call(section.querySelectorAll('[data-dw-option-input]'), function (input) {
      input.addEventListener('change', onOptionChange);
    });

    /* --------------- quantity stepper --------------- */
    function stepQty(delta) {
      if (!qtyInput) return;
      var value = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = Math.max(1, value + delta);
    }
    var minus = section.querySelector('[data-dw-qty-minus]');
    var plus = section.querySelector('[data-dw-qty-plus]');
    if (minus) minus.addEventListener('click', function () { stepQty(-1); });
    if (plus) plus.addEventListener('click', function () { stepQty(1); });
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        var value = parseInt(qtyInput.value, 10);
        if (isNaN(value) || value < 1) qtyInput.value = 1;
      });
    }

    /* --------------- add to cart --------------- */
    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || 'Could not add this to your cart. Please try again.';
      errorEl.hidden = false;
    }
    function hideError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    function setBusy(busy) {
      if (!atc) return;
      atc.classList.toggle('is-busy', busy);
      if (busy) {
        atc.setAttribute('aria-disabled', 'true');
        if (atcLabel) atcLabel.textContent = atc.getAttribute('data-label-adding');
      } else {
        atc.removeAttribute('aria-disabled');
      }
    }

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        /* Sold out: the button is the Back-in-Stock trigger, never add to
           cart (covers Enter-key submits the click bridge can't see). */
        if (atc && atc.hasAttribute('data-dw-notify-trigger')) return;
        if (!idInput || (atc && (atc.disabled || atc.classList.contains('is-busy')))) return;
        hideError();
        setBusy(true);
        /* Tells dw-cart.js's OCU rescue net this add is handled first-party */
        window.__dwAtcHandled = Date.now();

        var payload = {
          id: parseInt(idInput.value, 10),
          quantity: qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1
        };
        var planId = selectedPlanId();
        if (planId) payload.selling_plan = planId;

        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        })
          .then(function (response) {
            return response.json().then(function (json) {
              return { ok: response.ok, json: json };
            });
          })
          .then(function (result) {
            if (!result.ok) {
              var message = (result.json && (result.json.description || result.json.message)) || '';
              throw new Error(message);
            }
            if (atcLabel && atc) atcLabel.textContent = atc.getAttribute('data-label-added');
            /* Add succeeded; a failed drawer refresh (throttled /cart.js)
               must not turn into a "Try Again" for an item already in cart. */
            return fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
              .then(function (response) { return response.ok ? response.json() : null; })
              .catch(function () { return null; })
              .then(function (cart) {
                if (cart) {
                  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
                } else if (result.json && result.json.key) {
                  document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item: result.json } }));
                }
                if (window.DWCart && typeof window.DWCart.open === 'function') {
                  window.DWCart.open();
                }
              });
          })
          .catch(function (err) {
            showError(err && err.message);
            if (atcLabel && atc) atcLabel.textContent = atc.getAttribute('data-label-error');
          })
          .finally(function () {
            setBusy(false);
            window.setTimeout(updateAvailability, 2400);
          });
      });
    }

    /* --------------- sticky mobile bar --------------- */
    if (sticky && atc && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        var allow = mqMobile.matches || sticky.dataset.stickyDesktop === 'true';
        var show = allow && !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.hidden = !show;
      }, { threshold: 0 });
      observer.observe(atc);
    }

    /* --------------- initial sync --------------- */
    syncOptCards();
    updatePrices();
    updateAvailability();
  }

  /* ------------------------------------------------------------------ */
  /* Tabs / accordions                                                    */
  /* ------------------------------------------------------------------ */
  function initTabs(rootEl) {
    if (rootEl.dataset.dwReady) return;
    rootEl.dataset.dwReady = '1';

    var tabs = Array.prototype.slice.call(rootEl.querySelectorAll('[role="tab"]'));
    var panels = Array.prototype.slice.call(rootEl.querySelectorAll('[data-dw-panel]'));
    var accs = Array.prototype.slice.call(rootEl.querySelectorAll('[data-dw-acc]'));
    if (!panels.length) return;

    var mqMobile = window.matchMedia('(max-width: 767px)');

    function openPanel(panel, open) {
      panel.classList.toggle('is-open', open);
    }

    function selectTab(tab) {
      var target = tab.getAttribute('aria-controls');
      tabs.forEach(function (t) {
        var selected = t === tab;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.setAttribute('tabindex', selected ? '0' : '-1');
        t.classList.toggle('is-active', selected);
      });
      panels.forEach(function (panel) {
        openPanel(panel, panel.id === target);
      });
      accs.forEach(function (btn) {
        btn.setAttribute('aria-expanded', btn.getAttribute('aria-controls') === target ? 'true' : 'false');
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        selectTab(tab);
      });
      tab.addEventListener('keydown', function (event) {
        var idx = tabs.indexOf(tab);
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = tabs[(idx + 1) % tabs.length];
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = tabs[(idx - 1 + tabs.length) % tabs.length];
        else if (event.key === 'Home') next = tabs[0];
        else if (event.key === 'End') next = tabs[tabs.length - 1];
        if (next) {
          event.preventDefault();
          next.focus();
          selectTab(next);
        }
      });
    });

    accs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) openPanel(panel, !open);
      });
    });

    function syncToViewport() {
      if (mqMobile.matches) {
        // Accordion mode: honor aria-expanded state.
        accs.forEach(function (btn) {
          var panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (panel) openPanel(panel, btn.getAttribute('aria-expanded') === 'true');
        });
      } else if (tabs.length) {
        var active = null;
        tabs.forEach(function (t) {
          if (t.getAttribute('aria-selected') === 'true') active = t;
        });
        selectTab(active || tabs[0]);
      }
    }

    rootEl.classList.add('is-ready');
    if (tabs.length) selectTab(tabs[0]);
    syncToViewport();
    if (typeof mqMobile.addEventListener === 'function') {
      mqMobile.addEventListener('change', syncToViewport);
    } else if (typeof mqMobile.addListener === 'function') {
      mqMobile.addListener(syncToViewport);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Related products via the recommendations endpoint                    */
  /* ------------------------------------------------------------------ */
  function initRecs(el) {
    if (el.dataset.dwReady) return;
    el.dataset.dwReady = '1';
    var url = el.getAttribute('data-url');
    if (!url) return;
    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('Recommendations request failed');
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var grid = doc.querySelector('[data-dw-recommendations-grid][data-source="recommendations"]');
        if (!grid) return;
        // Skip products with no featured image (their cards render a placeholder SVG, not an <img>).
        Array.prototype.slice.call(grid.children).forEach(function (item) {
          if (!item.querySelector('img')) grid.removeChild(item);
        });
        if (grid.children.length) {
          el.innerHTML = grid.outerHTML;
        }
      })
      .catch(function () {
        /* keep the Liquid fallback grid */
      });
  }

  /* ------------------------------------------------------------------ */
  /* Boot + Theme Editor support                                          */
  /* ------------------------------------------------------------------ */
  function initAll(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-dw-product]'), initProduct);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-dw-tabs]'), initTabs);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-dw-recommendations]'), initRecs);
  }

  initAll(document);

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });
})();
